import { SelectChangeEvent } from '@mui/material/Select';
import { AxiosError } from 'axios';
import { FC, Fragment, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import {
  LoggingQuery,
  LoggingStatusCode,
  TypeService,
} from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  selectPermissionEvent,
  useAlertMessage,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  ErrorMessage,
  FeaturePage,
  fetchLogging,
  QUERY_KEY,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  ProtectedRouter,
  RefreshButton,
  ResetButton,
  Searchbox,
  SearchboxEmit,
  SearchboxOptions,
  Toolbar,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';
import { AdvancedSearch, AdvancedSearchCriteria } from './AdvancedSearch';
import { PermissionEventTable2 } from './PermissionEventTable2';

const PermissionEvent2: FC = () => {
  const resource = useResource();
  const { appName = '', clientNamePublic } = resource;
  const defaultParams = {
    appName: undefined,
    clientId: undefined,
    endDate: null,
    first: CONFIG.pagination.offset,
    max: CONFIG.pagination.step,
    search: undefined,
    startDate: null,
    status: null,
    type: null,
  };
  const defaultData = {
    items: [],
    meta: {
      first: 0,
      max: 0,
      totalRecord: 0,
    },
  };
  const { setAlertMessage } = useAlertMessage();
  const [params, setParams] = useState<LoggingQuery>({ ...defaultParams });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const isFilter = !!params.status || !!params.startDate || !!params.endDate;
  const queryKeys = [
    QUERY_KEY.PERMISSION_EVENT_LIST,
    appName,
    clientNamePublic,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(
    queryKeys,
    () =>
      fetchLogging({
        ...params,
        // Required when resource project selector changes since the
        // initial values of `params` state are not updated.
        appName,
        clientId: clientNamePublic,
        search: searchQuery,
      }),
    {
      onError: (error: AxiosError<ErrorMessage>) => {
        const message =
          error.response?.data.message || 'Cannot get permission events';

        setAlertMessage({
          message,
          statusCode: error.response?.status,
          typeStatusMessage: 'error',
        });
      },
      onSuccess: () => {
        if ((isSearch && isSearching) || (isFilter && isSearching)) {
          setIsSearching(false);
        }

        if (wasSearch && !isSearch && !isFilter) {
          setWasSearch(false);
        }

        if (!isSearch && !isFilter) {
          setPreviousOffset(params.first);
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const { meta } = data;
  const totalCount = meta.totalRecord;
  const totalRecord = meta.max;
  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    handleRefetch();
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged || isFilter);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword && !isFilter);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const hasKeyword = isSearch;
    const offset = previousOffset;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...defaultParams,
      first: offset,
      max: previous.max,
    }));

    if (hasKeyword) {
      setWasSearch(true);
      setSearchQuery(value);
    } else {
      handleRefetch();
    }
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      if (isFilter) {
        setIsSearching(true);
        setWasSearch(false);
        setSearchQuery(value);
      } else {
        handleSearchReset();
      }
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handleAdvancedSearchChange = (criteria: AdvancedSearchCriteria) => {
    const { endDate, startDate, status, type } = criteria;
    const hasCriteria = !!endDate || !!startDate || !!status || !!type;
    const offset = hasCriteria ? 0 : previousOffset;

    setIsSearching(hasCriteria);
    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      endDate,
      first: offset,
      startDate,
      status: status as LoggingStatusCode,
      type: type as TypeService,
    }));
    handleRefetch();
  };

  const handlePageChange = (offset: number) => {
    if (wasSearch) {
      setWasSearch(false);
    }

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPreviousOffset(offset);
    setPageStep(step);
    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));
    handleRefetch();
  };

  return (
    <Fragment>
      <Toolbar>
        <Toolbar.Control>
          <Toolbar.Search>
            <Searchbox
              autoFocus
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              onEnter={handleSearchEnter}
              placeholder="Search by username or email"
              value={searchQuery}
            />
            <AdvancedSearch
              onApply={handleAdvancedSearchChange}
              params={params}
            />
            <ResetButton
              disabled={!isSearch && !isFilter}
              onClick={handleSearchReset}
            >
              Reset Search
            </ResetButton>
          </Toolbar.Search>
          <Toolbar.Action>
            <RefreshButton onClick={handleRefresh}>Refresh</RefreshButton>
          </Toolbar.Action>
        </Toolbar.Control>
      </Toolbar>
      <PermissionEventTable2
        data={data.items}
        isFetched={isFetched}
        isFetching={isFetching}
        isLoading={isLoading}
        isFilter={isFilter}
        isProcessing={isProcessing}
        isSearch={isSearch || isFilter}
        isSearching={isSearching}
        onPageChange={handlePageChange}
        onStepChange={handleStepChange}
        paginationOffset={currentOffset}
        paginationStep={pageStep}
        totalCount={totalCount}
        totalRecord={totalRecord}
        wasSearch={wasSearch}
      />
    </Fragment>
  );
};

const WrappedPermissionEventPage: FC = () => {
  const { canViewPermission } = useSelector(selectPermissionEvent);

  return (
    <ProtectedRouter
      canView={canViewPermission}
      feature={FeaturePage.EVENT}
      isEditPage
      navigatePath="/events"
    >
      <PermissionEvent2 />
    </ProtectedRouter>
  );
};

export { WrappedPermissionEventPage };
